import { useRef, type PropsWithChildren, useEffect, useCallback, forwardRef } from 'react';

import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { Divider } from '@hh.ru/magritte-ui-divider';
import type { DividerState } from '@hh.ru/magritte-ui-drop/types';

import styles from './drop.less';

export const DropContainer = forwardRef<
    HTMLDivElement,
    PropsWithChildren<{
        dividerState: DividerState;
    }>
>(({ children, dividerState }, ref) => {
    const dividerRef = useRef<HTMLDivElement>(null);
    const showDividerRef = useRef(false);
    showDividerRef.current = dividerState !== 'hide';
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const contentContainerRefMulti = useMultipleRefs(contentContainerRef, ref);

    const updateDividerVisibility = useCallback(
        (showDivider: boolean) => {
            if (!contentContainerRef.current || !dividerRef.current) {
                return;
            }
            if (!showDivider) {
                dividerRef.current.style.visibility = 'hidden';
                return;
            }
            if (contentContainerRef?.current !== null && dividerRef.current) {
                const hasScroll =
                    contentContainerRef.current.scrollHeight - contentContainerRef.current.clientHeight > 0;
                dividerRef.current.style.visibility = hasScroll ? 'visible' : 'hidden';
            }
        },
        [contentContainerRef, dividerRef]
    );

    useEffect(() => {
        const observer = new ResizeObserver(() => {
            updateDividerVisibility(showDividerRef.current);
        });
        observer.observe(contentContainerRef.current as Element);
        return () => observer.disconnect();
    }, [updateDividerVisibility]);

    useEffect(() => {
        updateDividerVisibility(showDividerRef.current);
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [updateDividerVisibility, showDividerRef.current]);

    const props = showDividerRef.current
        ? {
              onScroll: ({ target }: { target: EventTarget }) => {
                  const scrollableContainer = target as HTMLDivElement;

                  if (dividerRef.current && dividerState !== 'showWhenFullyScrolled') {
                      const scrollEnded =
                          scrollableContainer.scrollTop +
                              scrollableContainer.offsetHeight -
                              scrollableContainer.scrollHeight ===
                          0;

                      dividerRef.current.style.visibility = scrollEnded ? 'hidden' : 'visible';
                  }
              },
          }
        : {};

    return (
        <>
            <div ref={contentContainerRefMulti} className={styles.dropContainer} {...props}>
                {children}
            </div>
            <div ref={dividerRef} className={styles.dividerContainer} data-qa="drop-divider">
                <Divider />
            </div>
        </>
    );
});

DropContainer.displayName = 'DropContainer';
